import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { PageContainer } from '@/components/common/PageContainer';
import { SuccessDialog } from '@/components/features/shipments/wizard/SuccessDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useApi } from '@/hooks/useApi';
import { useShippableCountries } from '@/hooks/useShippableCountries';
import { PhoneInput, isValidLocalPhone } from '@/components/ui/phone-input';
import { useAuthContext } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { ROUTES } from '@/lib/constants';
import { toast } from 'sonner';
import { Warehouse } from '@/types/warehouse.types';
import {
  ArrowRight,
  Building2,
  Truck,
  User,
  MapPin,
  Package,
  CreditCard,
  Star,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  CheckCircle2,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────

interface State { id: string; name: string; code: string; countryCode?: string; }
interface City { id: string; name: string; code: string; stateCode?: string; }
interface Neighborhood { id: string; name: string; name_ar?: string; }
interface CarrierConnection {
  id: string;
  carrier_id: string;
  is_default: boolean;
  carrier: { id: string; name: string; logo_url?: string };
}
interface TenantOption { id: string; name: string; phone?: string; subdomain?: string; name_translations?: Record<string, string>; }
interface ServiceType { id: string; code: string; name: string; }
interface ShipmentItem { name: string; quantity: number; sku: string; }

interface FormData {
  tenant_id: string;
  carrier_id: string;
  sender_warehouse_id: string;
  from_city_id: string;
  from_address: { street: string; city: string; neighborhood: string; lat: number; lng: number };
  service_type_id: string;
  to_state_code: string;
  to_city_id: string;
  to_neighborhood_id: string;
  to_address: { street: string; city: string; district: string; neighborhood: string };
  recipient_name: string;
  recipient_country: 'SA' | 'SD' | 'EG';
  recipient_phone: string;
  recipient_notes: string;
  items: ShipmentItem[];
  notes: string;
  shipment_value: number;
  delivery_fee: number;
  payment_type: 'cash' | 'bank_transfer' | 'online';
  payment_status: 'pending' | 'paid' | 'partial';
  payment_mode: 'sender_pays_all' | 'recipient_pays_fee' | 'recipient_pays_all';
}

const initialForm: FormData = {
  tenant_id: '',
  carrier_id: '',
  sender_warehouse_id: '',
  from_city_id: '',
  from_address: { street: '', city: '', neighborhood: '', lat: 0, lng: 0 },
  service_type_id: '',
  to_state_code: '',
  to_city_id: '',
  to_neighborhood_id: '',
  to_address: { street: '', city: '', district: '', neighborhood: '' },
  recipient_name: '',
  recipient_country: 'SA',
  recipient_phone: '',
  recipient_notes: '',
  items: [],
  notes: '',
  shipment_value: 0,
  delivery_fee: 0,
  payment_type: 'cash',
  payment_status: 'pending',
  payment_mode: 'recipient_pays_all',
};

// ─── Component ───────────────────────────────────────

export default function CreateShipment() {
  const navigate = useNavigate();
  const { post, get, loading } = useApi();
  const { user } = useAuthContext();
  const { tp } = useI18n();
  const role = user?.role || 'admin';
  const isTenant = role === 'tenant';
  const isCarrier = role === 'carrier';

  const [form, setForm] = useState<FormData>(initialForm);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [carriers, setCarriers] = useState<CarrierConnection[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const { countries: shippableCountries } = useShippableCountries();
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingFee, setLoadingFee] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [noRoute, setNoRoute] = useState(false);
  // Tenant carrier quotes for the selected lane, keyed by carrier_id.
  const [carrierQuotes, setCarrierQuotes] = useState<
    Record<string, { has_route: boolean; delivery_fee: number | null; currency: string | null }>
  >({});
  const [loadingQuotes, setLoadingQuotes] = useState(false);

  const set = (updates: Partial<FormData>) => setForm((p) => ({ ...p, ...updates }));

  // ─── Init ────────────────────────────────────────
  useEffect(() => {
    if (isTenant && user?.tenant_id) set({ tenant_id: user.tenant_id });
    if (isCarrier && user?.carrier_id) set({ carrier_id: user.carrier_id });
  }, []);

  useEffect(() => {
    get('/api/v1/lookups/shipment-types', { lang: 'ar' }).then((res: any) => {
      const items = Array.isArray(res?.items) ? res.items : [];
      setServiceTypes(items.map((s: any) => ({ id: s.value, code: s.code, name: s.label })));
    });
  }, []);

  // Recipient location cascade: country → district → city → neighborhood.
  // Each level reloads the next when its selection changes.
  useEffect(() => {
    if (!form.recipient_country) { setStates([]); return; }
    get('/api/v1/lookups/states', { country: form.recipient_country, lang: 'ar' })
      .then((res: any) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setStates(items.map((s: any) => ({ id: s.value, name: s.label, code: s.code, countryCode: s.country_code })));
      })
      .catch(() => setStates([]));
  }, [form.recipient_country]);

  useEffect(() => {
    if (!form.to_state_code) { setCities([]); return; }
    get('/api/v1/lookups/cities', { state: form.to_state_code, lang: 'ar' })
      .then((res: any) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setCities(items.map((c: any) => ({ id: c.value, name: c.label, code: c.code, stateCode: c.state_code })));
      })
      .catch(() => setCities([]));
  }, [form.to_state_code]);

  useEffect(() => {
    if (!isTenant || !form.tenant_id) return;
    get('/api/v1/tenants/my/carriers').then((res: any) => {
      const list = Array.isArray(res) ? res : [];
      setCarriers(list);
      const def = list.find((c: CarrierConnection) => c.is_default) || list[0];
      if (def) set({ carrier_id: def.carrier_id });
    });
  }, [form.tenant_id, isTenant]);

  useEffect(() => {
    if (isTenant) return;
    get('/api/v1/dashboard/tenants').then((res: any) => {
      const list = res?.list || [];
      setTenants(Array.isArray(list) ? list : []);
      if (list.length === 1) set({ tenant_id: list[0].id });
    });
  }, [isTenant]);

  useEffect(() => {
    if (!form.tenant_id) { setWarehouses([]); return; }
    setLoadingWarehouses(true);
    get(`/api/v1/dashboard/tenants/${form.tenant_id}/warehouses`)
      .then((res: any) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setWarehouses(list);
        const def = list.find((w: Warehouse) => w.is_default && w.is_active) || list.find((w: Warehouse) => w.is_active);
        if (def) selectWarehouse(def);
      })
      .finally(() => setLoadingWarehouses(false));
  }, [form.tenant_id]);

  useEffect(() => {
    if (!form.to_city_id) { setNeighborhoods([]); return; }
    get(`/api/v1/zones?city_id=${form.to_city_id}`).then((res: any) => {
      const list = res?.list || res || [];
      setNeighborhoods(Array.isArray(list) ? list : []);
    });
  }, [form.to_city_id]);

  // Admin / carrier: resolve the delivery fee for the single selected carrier.
  useEffect(() => {
    if (isTenant) return;
    if (!form.from_city_id || !form.to_city_id || !form.carrier_id || !form.service_type_id) {
      setNoRoute(false);
      return;
    }
    setLoadingFee(true);
    setNoRoute(false);
    get('/api/v1/dashboard/delivery-rates/calculate', {
      from_city_id: form.from_city_id,
      to_city_id: form.to_city_id,
      carrier_id: form.carrier_id,
      service_type_id: form.service_type_id,
      ...(form.tenant_id && { tenant_id: form.tenant_id }),
    })
      .then((res: any) => {
        const fee = res?.delivery_fee ?? res?.price ?? 0;
        set({ delivery_fee: Number(fee) });
        setNoRoute(false);
      })
      .catch(() => {
        set({ delivery_fee: 0 });
        setNoRoute(true);
      })
      .finally(() => setLoadingFee(false));
  }, [isTenant, form.from_city_id, form.to_city_id, form.carrier_id, form.service_type_id]);

  // Tenant: quote every connected carrier for the selected lane.
  useEffect(() => {
    if (!isTenant) return;
    if (!form.from_city_id || !form.to_city_id || !form.service_type_id) {
      setCarrierQuotes({});
      return;
    }
    setLoadingQuotes(true);
    get('/api/v1/dashboard/carriers/quotes', {
      from_city_id: form.from_city_id,
      to_city_id: form.to_city_id,
      service_type_id: form.service_type_id,
    })
      .then((res: any) => {
        const list: any[] = Array.isArray(res) ? res : [];
        const map: Record<string, { has_route: boolean; delivery_fee: number | null; currency: string | null }> = {};
        for (const q of list) {
          map[q.carrier_id] = {
            has_route: !!q.has_route,
            delivery_fee: q.delivery_fee ?? null,
            currency: q.currency ?? null,
          };
        }
        setCarrierQuotes(map);
      })
      .catch(() => setCarrierQuotes({}))
      .finally(() => setLoadingQuotes(false));
  }, [isTenant, form.from_city_id, form.to_city_id, form.service_type_id]);

  // Tenant: sync delivery_fee with the selected carrier's quote; if that carrier
  // has no route for the lane, fall back to the first routable carrier.
  useEffect(() => {
    if (!isTenant || loadingQuotes) return;
    if (!form.from_city_id || !form.to_city_id || !form.service_type_id) return;

    const selectedQuote = form.carrier_id ? carrierQuotes[form.carrier_id] : undefined;
    if (selectedQuote?.has_route) {
      const fee = Number(selectedQuote.delivery_fee ?? 0);
      if (Number(form.delivery_fee) !== fee) set({ delivery_fee: fee });
      return;
    }

    const fallback = carriers.find((c) => carrierQuotes[c.carrier_id]?.has_route);
    if (fallback) {
      set({
        carrier_id: fallback.carrier_id,
        delivery_fee: Number(carrierQuotes[fallback.carrier_id]?.delivery_fee ?? 0),
      });
    } else if (Number(form.delivery_fee) !== 0) {
      set({ delivery_fee: 0 });
    }
  }, [isTenant, loadingQuotes, carrierQuotes, form.carrier_id, form.from_city_id, form.to_city_id, form.service_type_id]);

  // ─── Helpers ─────────────────────────────────────

  const selectWarehouse = (wh: Warehouse) => {
    set({
      sender_warehouse_id: wh.id,
      from_city_id: wh.city_id || '',
      from_address: {
        street: wh.address || '',
        city: wh.city_name?.ar || '',
        neighborhood: '',
        lat: Number(wh.latitude) || 0,
        lng: Number(wh.longitude) || 0,
      },
    });
  };

  const addItem = () => set({ items: [...form.items, { name: '', quantity: 1, sku: '' }] });
  const removeItem = (i: number) => set({ items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i: number, field: keyof ShipmentItem, value: any) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    set({ items });
  };

  const totalAmount = Number(form.shipment_value || 0) + Number(form.delivery_fee || 0);

  // Whether enough inputs are set to have requested carrier quotes for the lane.
  const quotesAttempted =
    isTenant && !!(form.from_city_id && form.to_city_id && form.service_type_id);

  const getErrors = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.tenant_id) e.tenant_id = tp.createShipment.required;
    if (isTenant && !form.carrier_id) {
      e.carrier_id = tp.createShipment.selectCarrier;
    } else if (
      isTenant &&
      form.carrier_id &&
      quotesAttempted &&
      !loadingQuotes &&
      carrierQuotes[form.carrier_id] &&
      !carrierQuotes[form.carrier_id].has_route
    ) {
      e.carrier_id = tp.createShipment.noRouteCarrierError;
    }
    if (!form.sender_warehouse_id) e.sender_warehouse_id = tp.createShipment.selectWarehouse;
    if (!form.recipient_name.trim()) e.recipient_name = tp.createShipment.recipientNameRequired;
    if (!form.recipient_phone.trim()) e.recipient_phone = tp.createShipment.phoneRequired;
    else if (!isValidLocalPhone(form.recipient_phone, form.recipient_country))
      e.recipient_phone = tp.createShipment.invalidPhone;
    if (!form.to_state_code) e.to_state = tp.createShipment.districtRequired;
    if (!form.to_city_id) e.to_city = tp.createShipment.cityRequired;
    if (!form.to_neighborhood_id) e.to_neighborhood = tp.createShipment.neighborhoodRequired;
    if (!form.to_address.street.trim()) e.to_street = tp.createShipment.addressRequired;
    if (form.shipment_value < 0) e.shipment_value = tp.createShipment.invalidShipmentValue;
    return e;
  };

  const validate = (): boolean => {
    const e = getErrors();
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Drives the Create button: disabled until every required field is valid.
  const canSubmit = Object.keys(getErrors()).length === 0;

  const handleSubmitClick = () => {
    if (!validate()) { toast.error(tp.createShipment.fillAllFields); return; }
    setShowConfirmation(true);
  };

  const handleConfirmCreate = async () => {
    setSubmitting(true);
    try {
      const payload: Record<string, any> = {
        tenant_id: form.tenant_id,
        ...(form.carrier_id && { carrier_id: form.carrier_id }),
        sender_warehouse_id: form.sender_warehouse_id,
        from_address: form.from_address,
        ...(form.service_type_id && { service_type_id: form.service_type_id }),
        to_address: form.to_address,
        to_city_id: form.to_city_id || undefined,
        to_neighborhood_id: form.to_neighborhood_id || undefined,
        recipient_info: {
          name: form.recipient_name,
          country: form.recipient_country,
          phone: form.recipient_phone,
          notes: form.recipient_notes || undefined,
        },
        shipment_value: Number(form.shipment_value),
        delivery_fee: Number(form.delivery_fee),
        total_amount: totalAmount,
        payment_method: form.payment_type,
        payment_status: form.payment_status,
        payment_mode: form.payment_mode,
        ...(form.items.length > 0 && { items: form.items }),
        ...(form.notes && { notes: form.notes }),
      };

      const res: any = await post('/api/v1/dashboard/shipments/create', payload);
      const tracking = res?.tracking_number || res?.data?.tracking_number || 'N/A';
      setShowConfirmation(false);
      setTrackingNumber(tracking);
      setShowSuccess(true);
      toast.success(tp.createShipment.successMessage);
    } catch (error: any) {
      const msg = error.response?.data?.message || tp.createShipment.errorMessage;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (field: string) =>
    errors[field] ? (
      <p className="text-xs text-destructive flex items-center gap-1 mt-1">
        <AlertCircle className="h-3 w-3" />{errors[field]}
      </p>
    ) : null;

  // Derived
  const selectedCarrier = carriers.find((c) => c.carrier_id === form.carrier_id);
  const selectedWarehouse = warehouses.find((w) => w.id === form.sender_warehouse_id);
  const selectedServiceType = serviceTypes.find((s) => s.id === form.service_type_id);

  // ─── Render ──────────────────────────────────────

  return (
    <PageContainer>
      <div className="max-w-5xl mx-auto space-y-5" dir="rtl">

        {/* ── Header ── */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(ROUTES.SHIPMENTS)}>
            <ArrowRight className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{tp.createShipment.pageTitle}</h1>
            <p className="text-sm text-muted-foreground">{tp.createShipment.pageSubtitle}</p>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            CARD 1 — Shipment
        ════════════════════════════════════════════ */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4 text-primary" />
              {tp.createShipment.shipmentSection}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Tenant select (carrier / admin only) */}
            {!isTenant && (
              <div className="space-y-1.5">
                <Label>{tp.createShipment.customer} <span className="text-destructive">*</span></Label>
                <SearchableSelect
                  options={tenants.map((t) => ({
                    value: t.id,
                    label: [t.name || t.name_translations?.ar || t.subdomain, t.phone].filter(Boolean).join(' — '),
                  }))}
                  value={form.tenant_id}
                  onValueChange={(v) => set({ tenant_id: v, sender_warehouse_id: '', from_city_id: '' })}
                  placeholder={tp.createShipment.selectCustomer}
                  searchPlaceholder={tp.createShipment.search}
                />
                {fieldError('tenant_id')}
              </div>
            )}

            {/* Row: Service type | Warehouse */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{tp.createShipment.serviceType}</Label>
                <Select value={form.service_type_id} onValueChange={(v) => set({ service_type_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder={tp.createShipment.selectServiceType} />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((st) => (
                      <SelectItem key={st.id} value={st.id}>{st.name || st.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{tp.createShipment.warehouse} <span className="text-destructive">*</span></Label>
                {loadingWarehouses ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground h-10">
                    <Loader2 className="h-4 w-4 animate-spin" /> {tp.createShipment.loading}
                  </div>
                ) : (
                  <Select
                    value={form.sender_warehouse_id}
                    onValueChange={(v) => {
                      const wh = warehouses.find((w) => w.id === v);
                      if (wh) selectWarehouse(wh);
                    }}
                    disabled={warehouses.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={form.tenant_id ? tp.createShipment.selectWarehouse : tp.createShipment.selectCustomerFirst} />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.filter((w) => w.is_active).map((wh) => (
                        <SelectItem key={wh.id} value={wh.id}>
                          {wh.name} — {wh.city_name?.ar || ''}
                          {wh.is_default && <Badge variant="secondary" className="text-xs ms-1">{tp.createShipment.default}</Badge>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {fieldError('sender_warehouse_id')}
                {selectedWarehouse && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span>{selectedWarehouse.address} — {selectedWarehouse.city_name?.ar}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items table — always visible */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{tp.createShipment.products}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-3.5 w-3.5 ms-1" /> {tp.createShipment.addProduct}
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-right px-3 py-2.5 font-medium">{tp.createShipment.productName}</th>
                      <th className="text-right px-3 py-2.5 font-medium w-24">{tp.createShipment.quantity}</th>
                      <th className="text-right px-3 py-2.5 font-medium w-32">{tp.createShipment.sku}</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted-foreground py-6 text-xs">
                          {tp.createShipment.noProducts}
                        </td>
                      </tr>
                    ) : (
                      form.items.map((item, i) => (
                        <tr key={i} className={i % 2 ? 'bg-muted/20' : ''}>
                          <td className="px-3 py-1.5">
                            <Input
                              value={item.name}
                              onChange={(e) => updateItem(i, 'name', e.target.value)}
                              placeholder={tp.createShipment.productNamePlaceholder}
                              className="h-8 text-sm border-0 bg-transparent focus-visible:ring-1 px-0"
                            />
                          </td>
                          <td className="px-3 py-1.5">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(i, 'quantity', parseInt(e.target.value) || 1)}
                              className="h-8 text-sm w-20"
                            />
                          </td>
                          <td className="px-3 py-1.5">
                            <Input
                              value={item.sku}
                              onChange={(e) => updateItem(i, 'sku', e.target.value)}
                              placeholder={tp.createShipment.sku}
                              className="h-8 text-sm w-28"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem(i)}>
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notes — full row */}
            <div className="space-y-1.5">
              <Label>{tp.createShipment.notes}</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
                placeholder={tp.createShipment.notesPlaceholder}
                rows={2}
              />
            </div>

          </CardContent>
        </Card>

        {/* ════════════════════════════════════════════
            CARD 2 — Recipient   (+ Carrier, hidden for carrier users)
        ════════════════════════════════════════════ */}
        <div className="space-y-5">

          {/* Recipient */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4 text-primary" />
                {tp.createShipment.recipientSection}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Location cascade — country first (it drives the phone too) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1.5">
                  <Label>{tp.createShipment.country} <span className="text-destructive">*</span></Label>
                  <Select
                    value={form.recipient_country}
                    onValueChange={(v) =>
                      set({
                        recipient_country: v as 'SA' | 'SD' | 'EG',
                        to_state_code: '',
                        to_city_id: '',
                        to_neighborhood_id: '',
                        to_address: { ...form.to_address, district: '', city: '', neighborhood: '' },
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={tp.createShipment.countryPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {shippableCountries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{tp.createShipment.district} <span className="text-destructive">*</span></Label>
                  <SearchableSelect
                    options={states
                      .filter((s) => s.countryCode === form.recipient_country)
                      .map((s) => ({ value: s.code, label: s.name }))}
                    value={form.to_state_code}
                    onValueChange={(v) => {
                      const st = states.find((s) => s.code === v);
                      set({
                        to_state_code: v,
                        to_city_id: '',
                        to_neighborhood_id: '',
                        to_address: { ...form.to_address, district: st?.name || '', city: '', neighborhood: '' },
                      });
                    }}
                    placeholder={tp.createShipment.districtPlaceholder}
                    searchPlaceholder={tp.createShipment.search}
                  />
                  {fieldError('to_state')}
                </div>
                <div className="space-y-1.5">
                  <Label>{tp.createShipment.city} <span className="text-destructive">*</span></Label>
                  <SearchableSelect
                    options={cities
                      .filter((c) => c.stateCode === form.to_state_code)
                      .map((c) => ({ value: c.id, label: c.name }))}
                    value={form.to_city_id}
                    onValueChange={(v) => {
                      const city = cities.find((c) => c.id === v);
                      set({
                        to_city_id: v,
                        to_neighborhood_id: '',
                        to_address: { ...form.to_address, city: city?.name || '', neighborhood: '' },
                      });
                    }}
                    placeholder={tp.createShipment.cityPlaceholder}
                    searchPlaceholder={tp.createShipment.search}
                  />
                  {fieldError('to_city')}
                </div>
                <div className="space-y-1.5">
                  <Label>{tp.createShipment.neighborhood} <span className="text-destructive">*</span></Label>
                  <SearchableSelect
                    options={neighborhoods.map((n) => ({ value: n.id, label: n.name_ar || n.name }))}
                    value={form.to_neighborhood_id}
                    onValueChange={(v) => {
                      const nb = neighborhoods.find((n) => n.id === v);
                      set({
                        to_neighborhood_id: v,
                        to_address: { ...form.to_address, neighborhood: nb?.name_ar || nb?.name || '' },
                      });
                    }}
                    placeholder={tp.createShipment.neighborhoodPlaceholder}
                    searchPlaceholder={tp.createShipment.search}
                  />
                  {fieldError('to_neighborhood')}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>{tp.createShipment.detailedAddress} <span className="text-destructive">*</span></Label>
                <Input
                  value={form.to_address.street}
                  onChange={(e) => set({ to_address: { ...form.to_address, street: e.target.value } })}
                  placeholder={tp.createShipment.streetPlaceholder}
                />
                {fieldError('to_street')}
              </div>

              {/* Recipient contact — name + phone (phone uses the country above) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{tp.createShipment.name} <span className="text-destructive">*</span></Label>
                  <Input
                    value={form.recipient_name}
                    onChange={(e) => set({ recipient_name: e.target.value })}
                    placeholder={tp.createShipment.recipientName}
                  />
                  {fieldError('recipient_name')}
                </div>
                <div className="space-y-1.5">
                  <Label>{tp.createShipment.phone} <span className="text-destructive">*</span></Label>
                  <PhoneInput
                    country={form.recipient_country}
                    value={form.recipient_phone}
                    onChange={(v) => set({ recipient_phone: v })}
                    error={errors.recipient_phone}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>{tp.createShipment.recipientNotes}</Label>
                <Textarea
                  value={form.recipient_notes}
                  onChange={(e) => set({ recipient_notes: e.target.value })}
                  placeholder={tp.createShipment.notes}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Carrier — hidden for carrier users; they ship via their own account */}
          {!isCarrier && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Truck className="h-4 w-4 text-primary" />
                {tp.createShipment.carrierSection}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isTenant ? (
                /* Tenant: selectable carrier cards */
                carriers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
                    <Truck className="h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{tp.createShipment.noCarriers}</p>
                    <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.CARRIERS)}>
                      {tp.createShipment.addCarrier}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {!quotesAttempted && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5 pb-1">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {tp.createShipment.selectLocationForPrices}
                      </p>
                    )}
                    {carriers.map((c) => {
                      const selected = form.carrier_id === c.carrier_id;
                      const quote = carrierQuotes[c.carrier_id];
                      const noRouteCard =
                        quotesAttempted && !loadingQuotes && quote !== undefined && !quote.has_route;
                      return (
                        <button
                          key={c.carrier_id}
                          type="button"
                          disabled={noRouteCard}
                          onClick={() => { if (!noRouteCard) set({ carrier_id: c.carrier_id }); }}
                          className={`w-full text-right rounded-lg border p-3 transition-all ${
                            noRouteCard
                              ? 'border-border bg-muted/30 opacity-60 cursor-not-allowed'
                              : selected
                              ? 'border-primary bg-primary/5 ring-1 ring-primary'
                              : 'border-border hover:border-primary/40 hover:bg-muted/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {c.carrier.logo_url ? (
                              <img src={c.carrier.logo_url} alt={c.carrier.name} className="h-9 w-9 rounded-lg object-cover shrink-0" />
                            ) : (
                              <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                                <Truck className="h-4 w-4 text-primary" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-sm">{c.carrier.name}</span>
                              {c.is_default && (
                                <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs gap-1 ms-1.5">
                                  <Star className="h-3 w-3 fill-current" /> {tp.createShipment.default}
                                </Badge>
                              )}
                              {noRouteCard && (
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                  {tp.createShipment.noRouteForLane}
                                </span>
                              )}
                            </div>
                            {quotesAttempted && loadingQuotes ? (
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                            ) : quote?.has_route ? (
                              <Badge variant="secondary" className="text-xs shrink-0 font-semibold">
                                {Number(quote.delivery_fee ?? 0).toFixed(2)} {quote.currency || tp.createShipment.currencySymbol}
                              </Badge>
                            ) : null}
                            {selected && !noRouteCard && <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />}
                          </div>
                        </button>
                      );
                    })}
                    {fieldError('carrier_id')}
                  </div>
                )
              ) : (
                /* Admin: carrier is resolved automatically */
                <div className="flex flex-col items-center justify-center py-8 text-center gap-2 text-muted-foreground">
                  <Building2 className="h-8 w-8" />
                  <p className="text-sm">{tp.createShipment.carrierAutomatic}</p>
                </div>
              )}
            </CardContent>
          </Card>
          )}

        </div>

        {/* ════════════════════════════════════════════
            CARD 3 — Payment
        ════════════════════════════════════════════ */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4 text-primary" />
              {tp.createShipment.paymentSection}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Value | Fee | Total — same row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <div className="space-y-1.5">
                <Label>{tp.createShipment.shipmentValue} <span className="text-destructive">*</span></Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.shipment_value || ''}
                  onChange={(e) => set({ shipment_value: parseFloat(e.target.value) || 0 })}
                  placeholder={tp.createShipment.amountPlaceholder}
                />
                {fieldError('shipment_value')}
              </div>

              <div className="space-y-1.5">
                <Label className="flex items-center gap-2">
                  {tp.createShipment.deliveryFee}
                  {(loadingFee || loadingQuotes) && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                </Label>
                {/* Delivery fee is always route-driven — never typed by hand. */}
                <div className="relative">
                  <Input
                    type="number"
                    value={form.delivery_fee || ''}
                    disabled
                    className="bg-muted/50 pe-20"
                  />
                  <Badge variant="outline" className="absolute left-2 top-1/2 -translate-y-1/2 text-xs">{tp.createShipment.automatic}</Badge>
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 flex justify-between items-center h-10">
                <span className="text-sm text-muted-foreground">{tp.createShipment.total}</span>
                <span className="text-lg font-bold text-primary">{totalAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment mode — who pays what (drives COD + settlements) */}
            <div className="pt-3 border-t space-y-2">
              <Label>{tp.createShipment.paymentModeLabel}</Label>
              <RadioGroup
                value={form.payment_mode}
                onValueChange={(v: any) => set({ payment_mode: v })}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3"
              >
                {[
                  { value: 'recipient_pays_all', label: tp.createShipment.paymentModeRecipientPaysAll },
                  { value: 'recipient_pays_fee', label: tp.createShipment.paymentModeRecipientPaysFee },
                  { value: 'sender_pays_all',    label: tp.createShipment.paymentModeSenderPaysAll },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      form.payment_mode === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                    }`}
                  >
                    <RadioGroupItem value={opt.value} className="mt-0.5" />
                    <span className="text-sm leading-snug">{opt.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Payment type | Payment status — same row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-3 border-t">
              <div className="space-y-2">
                <Label>{tp.createShipment.paymentMethod}</Label>
                <RadioGroup
                  value={form.payment_type}
                  onValueChange={(v: any) => set({ payment_type: v })}
                  className="flex flex-wrap gap-4"
                >
                  {[
                    { value: 'cash', label: tp.createShipment.paymentCash },
                    { value: 'bank_transfer', label: tp.createShipment.paymentBankTransfer },
                    { value: 'online', label: tp.createShipment.paymentOnline },
                  ].map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value={opt.value} />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label>{tp.createShipment.paymentStatus}</Label>
                <RadioGroup
                  value={form.payment_status}
                  onValueChange={(v: any) => set({ payment_status: v })}
                  className="flex flex-wrap gap-4"
                >
                  {[
                    { value: 'pending', label: tp.createShipment.paymentPending },
                    { value: 'paid', label: tp.createShipment.paymentPaid },
                    { value: 'partial', label: tp.createShipment.paymentPartial },
                  ].map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <RadioGroupItem value={opt.value} />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>

          </CardContent>
        </Card>

        {/* ── Submit ── */}
        {isCarrier && noRoute && form.from_city_id && form.to_city_id && form.carrier_id && form.service_type_id && (
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-destructive/50 bg-destructive/10 mb-3">
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>لا يوجد تسعير لهذه الشحنة على هذا المسار</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs border-destructive text-destructive hover:bg-destructive/10 shrink-0"
              onClick={() => navigate('/dashboard/settings/pricing-routes')}
            >
              إضافة تسعير
            </Button>
          </div>
        )}
        {!canSubmit && (
          <p className="text-xs text-muted-foreground text-center mb-2 flex items-center justify-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {tp.createShipment.completeRequiredFields}
          </p>
        )}
        <Button
          className="w-full h-12 text-base"
          disabled={loading || submitting || !canSubmit || (isCarrier && noRoute)}
          onClick={handleSubmitClick}
        >
          <Truck className="h-5 w-5 ms-2" /> {tp.createShipment.submitButton}
        </Button>

      </div>

      {/* ════ Confirmation Modal ════ */}
      <Dialog open={showConfirmation} onOpenChange={(v) => !v && setShowConfirmation(false)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" dir="rtl">
          <DialogHeader className="text-right">
            <DialogTitle>{tp.createShipment.confirmTitle}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            {/* Sender */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tp.createShipment.senderSection}</p>
              <div className="bg-muted/40 rounded-lg p-3 space-y-1.5">
                {selectedWarehouse && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{tp.createShipment.warehouseLabel}</span>
                      <span className="font-medium">{selectedWarehouse.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{tp.createShipment.city}</span>
                      <span className="font-medium">{selectedWarehouse.city_name?.ar}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Recipient */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tp.createShipment.recipientConfirm}</p>
              <div className="bg-muted/40 rounded-lg p-3 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.name}</span>
                  <span className="font-medium">{form.recipient_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.phone}</span>
                  <span className="font-medium" dir="ltr">{form.recipient_phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.district}</span>
                  <span className="font-medium">{form.to_address.district}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.city}</span>
                  <span className="font-medium">{form.to_address.city}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.neighborhood}</span>
                  <span className="font-medium">{form.to_address.neighborhood}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.detailedAddress}</span>
                  <span className="font-medium text-left max-w-[55%]">{form.to_address.street}</span>
                </div>
              </div>
            </div>

            {/* Shipment */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tp.createShipment.shipmentConfirm}</p>
              <div className="bg-muted/40 rounded-lg p-3 space-y-1.5">
                {selectedServiceType && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{tp.createShipment.serviceTypeConfirm}</span>
                    <span className="font-medium">{selectedServiceType.name || selectedServiceType.code}</span>
                  </div>
                )}
                {form.items.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{tp.createShipment.itemsCount}</span>
                    <span className="font-medium">{form.items.length}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Carrier */}
            {selectedCarrier && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tp.createShipment.carrierConfirm}</p>
                <div className="bg-muted/40 rounded-lg p-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{tp.createShipment.carrierLabel}</span>
                    <span className="font-medium">{selectedCarrier.carrier.name}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Payment */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{tp.createShipment.paymentConfirm}</p>
              <div className="bg-muted/40 rounded-lg p-3 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.shipmentValueConfirm}</span>
                  <span className="font-medium">{form.shipment_value}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.deliveryFeeConfirm}</span>
                  <span className="font-medium">{form.delivery_fee}</span>
                </div>
                <div className="flex justify-between border-t pt-1.5 mt-1">
                  <span className="font-semibold">{tp.createShipment.totalAmount}</span>
                  <span className="font-bold text-primary text-base">{totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{tp.createShipment.paymentMethodConfirm}</span>
                  <span className="font-medium">
                    {form.payment_type === 'cash' ? tp.createShipment.paymentCash : form.payment_type === 'bank_transfer' ? tp.createShipment.paymentBankTransfer : tp.createShipment.electronicPayment}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col mt-2">
            <Button className="w-full" onClick={handleConfirmCreate} disabled={submitting}>
              {submitting
                ? <><Loader2 className="h-4 w-4 ms-2 animate-spin" /> {tp.createShipment.submitting}</>
                : <><CheckCircle2 className="h-4 w-4 ms-2" /> {tp.createShipment.confirmCreate}</>
              }
            </Button>
            <Button variant="outline" className="w-full" onClick={() => setShowConfirmation(false)} disabled={submitting}>
              {tp.createShipment.editData}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SuccessDialog
        open={showSuccess}
        onClose={() => { setShowSuccess(false); navigate(ROUTES.SHIPMENTS); }}
        trackingNumber={trackingNumber}
      />
    </PageContainer>
  );
}
